import * as React from "react";
import {
    DataGrid,
    gridClasses,
    GridToolbar,
    GridValidRowModel,
} from "@mui/x-data-grid";
import { alpha, styled } from "@mui/material/styles";
import { DataGridProps } from "@mui/x-data-grid/models/props/DataGridProps";
import { constVoid, flow, pipe } from "fp-ts/function";
import { array, option } from "fp-ts";

const evenClassName = "even";
const oddClassName = "odd";

interface DataTableProps<R extends GridValidRowModel>
    extends Pick<
        DataGridProps<R>,
        "loading" | "columns" | "rows" | "initialState" | "components"
    > {}
const CustomDataGrid = <R extends GridValidRowModel>(p: DataTableProps<R>) => {
    return (
        <DataGrid
            {...p}
            components={{ Toolbar: GridToolbar, ...p.components }}
            componentsProps={{
                toolbar: {
                    printOptions: { disableToolbarButton: true }, // TODO print does not work in xathlo project setup :/
                    csvOptions: {
                        delimiter: ";",
                        utf8WithBom: true,
                    },
                },
            }}
            disableDensitySelector
            disableSelectionOnClick
            density={"compact"}
            getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0
                    ? evenClassName
                    : oddClassName
            }
        />
    );
};

const ODD_OPACITY = 0.1;
const StyledDataTable = styled(CustomDataGrid)(({ theme }) => ({
    [`& .${gridClasses.row}`]: {
        // has to be before hover styles!
        [`&.${evenClassName}`]: {
            backgroundColor: theme.palette.grey[200],
        },
        "&:hover, &.Mui-hovered": {
            backgroundColor: alpha(
                theme.palette.primary.main,
                ODD_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity
            ),
            // Reset on touch devices, it doesn't add specificity
            "@media (hover: none)": {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY + theme.palette.action.selectedOpacity
                ),
            },
        },
    },
}));

export const DataTable = StyledDataTable as typeof CustomDataGrid; // styled HOC damages typing

export const useRemoveLicenseWarningEffect = (
    rootRef: React.RefObject<HTMLDivElement>
) => {
    React.useEffect(() => {
        const intervalId = setInterval(() => {
            pipe(
                rootRef.current,
                option.fromNullable,
                option.chain((rootDiv) =>
                    option.fromNullable(
                        rootDiv.getElementsByClassName("MuiDataGrid-root")[0]
                    )
                ),
                option.chain((dataGridRootDiv) =>
                    option.fromNullable(
                        dataGridRootDiv.getElementsByClassName(
                            "MuiDataGrid-main"
                        )[0]
                    )
                ),
                option.chain(
                    flow(
                        (mainDiv) => Array.from(mainDiv.children),
                        array.findFirst(
                            (potentialLicenceWarningDiv) =>
                                potentialLicenceWarningDiv.innerHTML ===
                                "MUI X: Missing license key"
                        )
                    )
                ),
                option.fold(constVoid, (licenceWarningDiv) => {
                    licenceWarningDiv.remove();
                })
            );
        }, 200);

        return () => {
            clearInterval(intervalId);
        };
    }, [rootRef.current]);
};
